/**
 * EU/UK country data and the device-level signals (timezone, language) that
 * point at it. Split out of `euUk.ts` so browser code (the tracker consent
 * default in `src/features/consent/policy.ts`) can use it without loading the
 * phone-number parser. `euUk.ts` re-exports everything here.
 *
 * Pure module: no I/O.
 */

/** EU member states (ISO 3166-1 alpha-2). */
const EU_MEMBER_STATES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE",
  "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
] as const;

/** EEA states outside the EU (GDPR applies through the EEA Agreement). */
const EEA_NON_EU = ["IS", "LI", "NO"] as const;

/**
 * EU territories with their own ISO code (Åland, French outermost regions).
 * GDPR applies there.
 */
const EU_TERRITORIES = ["AX", "GF", "GP", "MQ", "RE", "YT", "MF"] as const;

/**
 * The UK, plus Gibraltar and the Crown Dependencies. The last group has its
 * own GDPR-equivalent laws; we treat it like the UK to stay on the safe side.
 */
const UK_AND_CLOSE = ["GB", "GI", "GG", "JE", "IM"] as const;

export const EU_UK_COUNTRIES: ReadonlySet<string> = new Set<string>([
  ...EU_MEMBER_STATES,
  ...EEA_NON_EU,
  ...EU_TERRITORIES,
  ...UK_AND_CLOSE,
]);

/** Common non-ISO spellings we may see in stored data. */
const COUNTRY_ALIASES: Readonly<Record<string, string>> = {
  UK: "GB",
  EL: "GR",
};

/** IANA zones that sit inside an EU/UK country, mapped to that country. */
const EU_UK_TIME_ZONES: Readonly<Record<string, string>> = {
  "Europe/Amsterdam": "NL",
  "Europe/Athens": "GR",
  "Europe/Belfast": "GB",
  "Europe/Berlin": "DE",
  "Europe/Bratislava": "SK",
  "Europe/Brussels": "BE",
  "Europe/Bucharest": "RO",
  "Europe/Budapest": "HU",
  "Europe/Busingen": "DE",
  "Europe/Copenhagen": "DK",
  "Europe/Dublin": "IE",
  "Europe/Gibraltar": "GI",
  "Europe/Guernsey": "GG",
  "Europe/Helsinki": "FI",
  "Europe/Isle_of_Man": "IM",
  "Europe/Jersey": "JE",
  "Europe/Lisbon": "PT",
  "Europe/Ljubljana": "SI",
  "Europe/London": "GB",
  "Europe/Luxembourg": "LU",
  "Europe/Madrid": "ES",
  "Europe/Malta": "MT",
  "Europe/Mariehamn": "AX",
  "Europe/Nicosia": "CY",
  "Europe/Oslo": "NO",
  "Europe/Paris": "FR",
  "Europe/Prague": "CZ",
  "Europe/Riga": "LV",
  "Europe/Rome": "IT",
  "Europe/Sofia": "BG",
  "Europe/Stockholm": "SE",
  "Europe/Tallinn": "EE",
  "Europe/Vaduz": "LI",
  "Europe/Vienna": "AT",
  "Europe/Vilnius": "LT",
  "Europe/Warsaw": "PL",
  "Europe/Zagreb": "HR",
  "Asia/Nicosia": "CY",
  "Asia/Famagusta": "CY",
  "Atlantic/Azores": "PT",
  "Atlantic/Canary": "ES",
  "Atlantic/Madeira": "PT",
  "Atlantic/Reykjavik": "IS",
  "Africa/Ceuta": "ES",
  "America/Cayenne": "GF",
  "America/Guadeloupe": "GP",
  "America/Marigot": "MF",
  "America/Martinique": "MQ",
  "Indian/Mayotte": "YT",
  "Indian/Reunion": "RE",
  GB: "GB",
  "GB-Eire": "GB",
  Eire: "IE",
  Iceland: "IS",
  Poland: "PL",
  Portugal: "PT",
  // Legacy region-less zones. IANA 2024b links each to one EU city
  // (CET/MET to Brussels, EET to Athens, WET to Lisbon); only European
  // systems report them.
  CET: "BE",
  MET: "BE",
  EET: "GR",
  WET: "PT",
};

export function normalizeCountryCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const upper = value.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return null;
  return COUNTRY_ALIASES[upper] ?? upper;
}

export function isEuUkCountry(value: unknown): boolean {
  const code = normalizeCountryCode(value);
  return code !== null && EU_UK_COUNTRIES.has(code);
}

export function countryFromLocale(language: unknown): string | null {
  if (typeof language !== "string" || !language.trim()) return null;
  try {
    const region = new Intl.Locale(language.trim().replace(/_/g, "-")).region;
    return normalizeCountryCode(region ?? null);
  } catch {
    return null;
  }
}

/** Only returns a country for zones inside an EU/UK country. */
export function countryFromTimeZone(timeZone: unknown): string | null {
  if (typeof timeZone !== "string" || !timeZone.trim()) return null;
  return EU_UK_TIME_ZONES[timeZone.trim()] ?? null;
}
